import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getTokenExpiry,
  checkTokenExpired,
  removeTokenExpiry,
  setTokenExpiry,
  isTimeWithinWarningThreshold,
} from '../utils/tokenStorage';

type TokenTimeoutState = {
  timeUntilExpiry: number | null;
  isTokenExpiringSoon: boolean;
  isTokenExpired: boolean;
  storedExpiryTimestamp: number | null;
};

type TokenTimeoutActions = {
  setTokenExpiryTime: (expiryTimestamp: number) => void;
  clearTokenExpiry: () => void;
  onTokenExpired: () => void;
  onTokenExpiringSoon: () => void;
};

type UseTokenTimeoutProps = {
  onTokenExpired?: () => Promise<void> | void;
  onTokenExpiringSoon?: () => void;
};

export const useTokenTimeout = ({
  onTokenExpired,
  onTokenExpiringSoon,
}: UseTokenTimeoutProps = {}): TokenTimeoutState & TokenTimeoutActions => {
  const [storedExpiryTimestamp, setStoredExpiryTimestamp] = useState<number | null>(null);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef<boolean>(false);

  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current != null) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    if (expiryTimeoutRef.current != null) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearTimers();
    setStoredExpiryTimestamp(null);
    setTimeUntilExpiry(null);
    warningShownRef.current = false;
  }, [clearTimers]);

  const handleTokenExpired = useCallback(async () => {
    resetState();
    await onTokenExpired?.();
  }, [resetState, onTokenExpired]);

  const handleTokenExpiringSoon = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      onTokenExpiringSoon?.();
    }
  }, [onTokenExpiringSoon]);

  const startTracking = useCallback(
    (expiry: number) => {
      // Clear any existing timers first
      resetState();

      // Set up new tracking state
      setStoredExpiryTimestamp(expiry);

      const now = Date.now();
      const initialTimeRemaining = expiry - now;
      setTimeUntilExpiry(initialTimeRemaining);

      // If token is already expired, handle immediately
      if (initialTimeRemaining <= 0) {
        void handleTokenExpired();
        return;
      }

      // Calculate when to show warning (3 minutes before expiry)
      const warningTime = expiry - 3 * 60 * 1000; // 3 minutes before expiry
      const timeUntilWarning = warningTime - now;

      // Set up warning timeout if we haven't reached warning time yet
      if (timeUntilWarning > 0 && !warningShownRef.current) {
        warningTimeoutRef.current = setTimeout(() => {
          handleTokenExpiringSoon();
        }, timeUntilWarning);
      } else if (timeUntilWarning <= 0 && !warningShownRef.current) {
        // We're already in warning period, show warning immediately
        handleTokenExpiringSoon();
      }

      // Set up expiry timeout
      expiryTimeoutRef.current = setTimeout(() => {
        void handleTokenExpired();
      }, initialTimeRemaining);
    },
    [resetState, handleTokenExpired, handleTokenExpiringSoon],
  );

  const setTokenExpiryTime = useCallback(
    (expiryTimestamp: number) => {
      // Convert seconds to milliseconds if needed (JWT exp is in seconds)
      const expiry = expiryTimestamp < 10000000000 ? expiryTimestamp * 1000 : expiryTimestamp;

      // Store in persistent storage
      void setTokenExpiry(expiry);

      // Start tracking
      startTracking(expiry);
    },
    [startTracking],
  );

  const clearTokenExpiry = useCallback(() => {
    resetState();
    void removeTokenExpiry();
  }, [resetState]);

  // Load stored expiry on mount and restart tracking
  useEffect(() => {
    const loadStoredExpiry = async (): Promise<void> => {
      const storedExpiry = await getTokenExpiry();
      if (storedExpiry != null) {
        const now = Date.now();
        if (storedExpiry > now) {
          // Token is still valid, restart tracking
          startTracking(storedExpiry);
        } else {
          // Token has already expired, clean up
          clearTokenExpiry();
        }
      }
    };

    void loadStoredExpiry();
  }, [startTracking, clearTokenExpiry]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  const isTokenExpiringSoon =
    storedExpiryTimestamp != null && isTimeWithinWarningThreshold(storedExpiryTimestamp);

  const isTokenExpired = storedExpiryTimestamp != null && checkTokenExpired(storedExpiryTimestamp);

  return {
    timeUntilExpiry,
    isTokenExpiringSoon,
    isTokenExpired,
    storedExpiryTimestamp,
    setTokenExpiryTime,
    clearTokenExpiry,
    onTokenExpired: handleTokenExpired,
    onTokenExpiringSoon: handleTokenExpiringSoon,
  };
};
